// Pure-function dispatcher for notification-banner actions on the web.
//
// When the user taps "Reply" / "Mark as read" / "Mute" on a notification
// banner, the Service Worker's `notificationclick` handler receives a
// `NotificationEvent` with `event.action` (one of our registered action ids
// or an empty string for a default tap) and, for text-input actions,
// `event.reply` (the cleartext the user typed).
//
// The adapter translates that event into a `NotificationActionContext` and
// calls `NotificationActionDispatcher.dispatch`. This module decides what
// to do: encrypt-and-send the reply, mark a message read, mute a thread,
// or ignore. It never touches the network or shared singletons, so the
// self-test can enumerate every outcome.
//
// Threat model
// ─────────────
// 1. Reply-from-notification rides the same E2EE pipeline as in-app sends.
//    The user's typed cleartext is the *plaintext input* to encrypt; this
//    file emits a `{ kind: 'sendReply' }` outcome and the caller passes the
//    text into the E2EE encrypt pipeline. There is no parallel "notification
//    reply" transport.
//
// 2. Blocked-sender refusal. If the user has blocked the sender, an inline
//    reply field would let the user inadvertently send to a peer they have
//    cut off. We refuse to construct the reply outcome and emit
//    `{ kind: 'ignored', reason: 'blocked sender' }` — the caller drops it
//    silently so we do not surface a "blocked" toast on the lock screen.
//
// 3. Empty / oversize / whitespace-only text is dropped before the reply
//    leaves the device, preventing zero-byte / pathological encrypts.
//
// 4. The `ignored` reason strings are *constant string literals*. They
//    never include `userText`, `senderUserId`, or `threadIdentifier`, so
//    the cleartext the user typed cannot leak into a log line via a
//    formatted reason string.
//
// 5. `actionId` is typed as `NotificationActionId` (closed union). The
//    Service Worker adapter maps the raw string with a strict exhaustive
//    check and short-circuits on unknown ids — a peer-supplied or corrupted
//    action id never reaches this dispatcher.
//
// Mirror of `kaether_for_ios/Stoat/E2EE/NotificationActionHandler.swift`.

import type { NotificationActionId } from "./NotificationCategory";

// ---------------------------------------------------------------------------
// Context + outcome types
// ---------------------------------------------------------------------------

export interface NotificationActionContext {
  readonly actionId: NotificationActionId;
  readonly threadIdentifier: string;
  readonly senderUserId: string;
  readonly messageId: string;
  readonly userText: string | null;
  readonly isThreadMuted: boolean;
  readonly isSenderBlocked: boolean;
}

export type NotificationActionOutcome =
  | { readonly kind: "sendReply"; readonly threadIdentifier: string; readonly senderUserId: string; readonly text: string }
  | { readonly kind: "markRead"; readonly messageId: string }
  | { readonly kind: "muteThread"; readonly threadIdentifier: string; readonly durationSeconds: number }
  | { readonly kind: "ignored"; readonly reason: string };

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

export const NotificationActionDispatcher = {
  /**
   * Maximum cleartext length accepted from the system text field.
   * Matches iOS `NotificationActionDispatcher.maxReplyTextCount` (4 000)
   * and the in-app composer cap so reply-from-notification can never
   * produce a message the regular composer would reject.
   */
  maxReplyTextCount: 4_000,

  /**
   * Mute duration for the `mute1h` quick action, in seconds.
   */
  mute1hDurationSeconds: 3_600,

  /**
   * Dispatch a notification action context to an outcome.
   *
   * Pure: no side-effects, no network, no storage.
   */
  dispatch(ctx: NotificationActionContext): NotificationActionOutcome {
    switch (ctx.actionId) {
      case "quickReply":
        return dispatchQuickReply(ctx);
      case "markRead":
        return { kind: "markRead", messageId: ctx.messageId };
      case "mute1h":
        if (ctx.isThreadMuted) {
          return { kind: "ignored", reason: "already muted" };
        }
        return {
          kind: "muteThread",
          threadIdentifier: ctx.threadIdentifier,
          durationSeconds: NotificationActionDispatcher.mute1hDurationSeconds,
        };
    }
  },
} as const;

// ---------------------------------------------------------------------------
// Quick reply logic (private to this module)
// ---------------------------------------------------------------------------

function dispatchQuickReply(ctx: NotificationActionContext): NotificationActionOutcome {
  // Block first — refuse to even consider the typed text.
  if (ctx.isSenderBlocked) {
    return { kind: "ignored", reason: "blocked sender" };
  }

  const raw = ctx.userText ?? "";
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { kind: "ignored", reason: "empty text" };
  }
  if (trimmed.length > NotificationActionDispatcher.maxReplyTextCount) {
    return { kind: "ignored", reason: "too long" };
  }

  return {
    kind: "sendReply",
    threadIdentifier: ctx.threadIdentifier,
    senderUserId: ctx.senderUserId,
    text: trimmed,
  };
}
